import React, { useState, useEffect } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { Save, FileText, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://api.marketmaestroovip.in';

const FIELDS = [
  // group label, list of { key, label, type }
  {
    group: 'Page Header',
    icon: '📄',
    fields: [
      { key: 'page_title', label: 'Page Title', type: 'text' },
      { key: 'page_subtitle', label: 'Page Subtitle', type: 'text' },
      { key: 'sebi_line', label: 'SEBI Line', type: 'text' },
      { key: 'last_updated', label: 'Last Updated', type: 'text' }
    ]
  },
  {
    group: '1. Acceptance of Terms',
    icon: '📜',
    fields: [
      { key: 'acceptance_title', label: 'Title', type: 'text' },
      { key: 'acceptance_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '2. Eligibility',
    icon: '👤',
    fields: [
      { key: 'eligibility_title', label: 'Title', type: 'text' },
      { key: 'eligibility_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '3. Our Services',
    icon: '🛡️',
    fields: [
      { key: 'services_title', label: 'Title', type: 'text' },
      { key: 'services_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '4. SEBI Compliance',
    icon: '⚖️',
    fields: [
      { key: 'sebi_title', label: 'Title', type: 'text' },
      { key: 'sebi_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '5. Risk Disclosure',
    icon: '⚠️',
    fields: [
      { key: 'risk_title', label: 'Title', type: 'text' },
      { key: 'risk_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '6. Prohibited Conduct',
    icon: '🚫',
    fields: [
      { key: 'prohibited_title', label: 'Title', type: 'text' },
      { key: 'prohibited_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '7. Modifications',
    icon: '🔄',
    fields: [
      { key: 'modifications_title', label: 'Title', type: 'text' },
      { key: 'modifications_content', label: 'Content', type: 'textarea' }
    ]
  },
  {
    group: '8. Contact',
    icon: '📞',
    fields: [
      { key: 'contact_title', label: 'Title', type: 'text' },
      { key: 'contact_content', label: 'Content', type: 'textarea' },
      { key: 'contact_email', label: 'Email', type: 'text' },
      { key: 'contact_phone', label: 'Phone', type: 'text' }
    ]
  }
];

const TermsManager = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeGroup, setActiveGroup] = useState(0);

  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [sidebarOpen]);

  // Load terms
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`${API_URL}/api/terms`);
        setFormData(res.data.data || {});
      } catch (err) {
        console.error('Error fetching terms:', err);
        setMessage({ type: 'error', text: 'Failed to load terms content.' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchTerms();
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      // Send only editable fields (exclude id/created_at/updated_at)
      const payload = { ...formData };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;

      const res = await axios.put(`${API_URL}/api/terms`, payload);

      if (res.data.success) {
        setFormData(res.data.data || payload);
        setMessage({ type: 'success', text: 'Terms & Conditions updated successfully!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Update failed.' });
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        <Sidebar isOpen={sidebarOpen} />

        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Title */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                  <FileText className="text-blue-600" />
                  Manage Terms & Conditions
                </h1>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                  Update existing sections. New sections cannot be added from here.
                </p>
              </div>

              <button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" /> Save Changes
                  </>
                )}
              </button>
            </div>

            {/* Status message */}
            {message && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {message.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
                {message.text}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar of groups */}
                <aside className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 sticky top-4">
                    <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase mb-3 px-2">
                      Sections
                    </h3>
                    <nav className="space-y-1">
                      {FIELDS.map((g, i) => (
                        <button
                          key={g.group}
                          onClick={() => setActiveGroup(i)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                            activeGroup === i
                              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span>{g.icon}</span>
                          <span className="truncate">{g.group}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                </aside>

                {/* Form */}
                <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <span>{FIELDS[activeGroup].icon}</span>
                    {FIELDS[activeGroup].group}
                  </h2>

                  <div className="space-y-5">
                    {FIELDS[activeGroup].fields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          {f.label}
                        </label>
                        {f.type === 'textarea' ? (
                          <textarea
                            rows={8}
                            value={formData[f.key] || ''}
                            onChange={(e) => handleChange(f.key, e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition font-mono text-sm leading-relaxed"
                            placeholder="Use Enter to separate paragraphs"
                          />
                        ) : (
                          <input
                            type="text"
                            value={formData[f.key] || ''}
                            onChange={(e) => handleChange(f.key, e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Bottom Save */}
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" /> Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default TermsManager;